import { useAppDispatch, useAppSelector } from "../../../app/store";
import {
    changeModal,
    closeModal,
    EditUserPayload,
    NewUserPayload,
    getUser,
    saveNewUser,
    saveUser, deleteUser
} from "../../../app/slices/modals/userModal";
import { getSelectOptions } from "../../../app/slices/admin";
import { Col, Modal, Form, Button, Alert } from "react-bootstrap";
import AdminLoader from "../AdminLoader";
import React, { useEffect, useCallback, useState } from "react";
import { ADMIN_LANG } from "../../../settings";
import { useNavigate } from "react-router-dom";
import AsyncTeamsSelect from "../gameView/AsyncTeamsSelect";
import { PlainDetails } from "../../../types/admin";
import Confirm from "../Confirm";
import { isFulfilled } from "@reduxjs/toolkit";

const UserModal = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const {
        isOpen,
        loading,
        error,
        user_id,
        first_name,
        last_name,
        company,
        username,
        email,
        is_active,
        is_staff,
        is_superuser,
        password1,
        password2,
        teams,
        team_options,
        delete_message
    } = useAppSelector((state) => state.userModal);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);


    useEffect(() => {
        if (isOpen && user_id) {
            dispatch(getUser(user_id));
        } else if (isOpen && !user_id) {
            dispatch(getSelectOptions(['team_options']));
        }
    }, [dispatch, isOpen, user_id]);

    const handleClose = useCallback(() => {
        dispatch(closeModal());
    }, [dispatch]);

    const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const modalForm = e.currentTarget;

        if (!modalForm.checkValidity()) {
            e.stopPropagation();
        } else {
            const payload = {
                user_id,
                first_name,
                last_name,
                company,
                username,
                email,
                is_active,
                is_staff,
                is_superuser,
                password1,
                password2,
                teams
            };

            const action = user_id ? saveUser(payload as EditUserPayload) : saveNewUser(payload as NewUserPayload);

            dispatch(action).then((resultAction) => {
                let newId = '0'
                if (resultAction.payload && resultAction.payload.user_id) {
                    newId = resultAction.payload.user_id
                }
                if (!user_id && isFulfilled(resultAction)) {
                    navigate(`/admin/users/?new=${newId}`);
                }
            });
        }
    };

    const handleDelete = () => {
        if (user_id) {
            setShowConfirmDelete(true);
        }
    };

    const handleConfirm = () => {
        setShowConfirmDelete(false);
        if (user_id) {
            dispatch(deleteUser(user_id)).then((resultAction) => {
                if (isFulfilled(resultAction)) {
                    navigate('/admin/users');
                }
            });
        }
    };

    const handleTeamsChange = useCallback((selectedValues: PlainDetails[]) => {
        dispatch(changeModal({ teams: selectedValues }));
    }, [dispatch]);

    const modalTitle = user_id ? ADMIN_LANG.edit_user : ADMIN_LANG.new_user;

    return (
        <Modal size='xl' show={isOpen} onHide={handleClose} className='admin-modal'>
            {loading && <AdminLoader />}
            <Modal.Header closeButton>
                <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant='danger' dismissible>
                        <div dangerouslySetInnerHTML={{ __html: error }} />
                    </Alert>
                )}
                <Form id='user-modal-form' className='row gy-3' onSubmit={handleSave}>
                    <Col xs={6}>
                        <Form.Group controlId='first_name'>
                            <Form.Label>{ADMIN_LANG.first_name}</Form.Label>
                            <Form.Control
                                maxLength={255}
                                type='text'
                                required
                                value={first_name}
                                onChange={(e) => dispatch(changeModal({ first_name: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={6}>
                        <Form.Group controlId='last_name'>
                            <Form.Label>{ADMIN_LANG.last_name}</Form.Label>
                            <Form.Control
                                maxLength={255}
                                type='text'
                                required
                                value={last_name}
                                onChange={(e) => dispatch(changeModal({ last_name: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group controlId='company'>
                            <Form.Label>{ADMIN_LANG.company}</Form.Label>
                            <Form.Control
                                maxLength={255}
                                type='text'
                                value={company}
                                onChange={(e) => dispatch(changeModal({ company: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group controlId='username'>
                            <Form.Label>{ADMIN_LANG.username}</Form.Label>
                            <Form.Control
                                maxLength={255}
                                type='text'
                                value={username}
                                onChange={(e) => dispatch(changeModal({ username: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12}>
                        <Form.Group controlId='email'>
                            <Form.Label>{ADMIN_LANG.email}</Form.Label>
                            <Form.Control
                                maxLength={255}
                                type='email'
                                value={email}
                                onChange={(e) => dispatch(changeModal({ email: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={6}>
                        <Form.Group controlId='password1'>
                            <Form.Label>{ADMIN_LANG.password}</Form.Label>
                            <Form.Control
                                maxLength={255}
                                type='password'
                                value={password1}
                                onChange={(e) => dispatch(changeModal({ password1: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={6}>
                        <Form.Group controlId='password2'>
                            <Form.Label>{ADMIN_LANG.repeat_password}</Form.Label>
                            <Form.Control
                                maxLength={255}
                                type='password'
                                value={password2}
                                onChange={(e) => dispatch(changeModal({ password2: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12}>
                        <Form.Group controlId='is_active_modal'>
                            <Form.Switch
                                label={ADMIN_LANG.is_active}
                                checked={is_active}
                                className='form-label'
                                onChange={(e) => dispatch(changeModal({ is_active: e.target.checked }))}
                            />
                        </Form.Group>
                        <Form.Group controlId='is_staff_modal'>
                            <Form.Switch
                                label={ADMIN_LANG.is_staff}
                                checked={is_staff}
                                className='form-label'
                                onChange={(e) => dispatch(changeModal({ is_staff: e.target.checked }))}
                            />
                        </Form.Group>
                        <Form.Group controlId='is_superuser_modal'>
                            <Form.Switch
                                label={ADMIN_LANG.is_superuser}
                                checked={is_superuser}
                                className='form-label'
                                onChange={(e) => dispatch(changeModal({ is_superuser: e.target.checked }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12}>
                        <Form.Group controlId='teams'>
                            <Form.Label className='form-label'>{ADMIN_LANG.teams}</Form.Label>
                            <AsyncTeamsSelect selectedTeams={teams} initialValues={team_options} onChange={handleTeamsChange} />
                        </Form.Group>
                    </Col>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {user_id &&
                    <Button variant='danger' className='me-auto' onClick={handleDelete}>
                        {ADMIN_LANG.delete}
                    </Button>
                }
                <Button variant='outline-primary' onClick={handleClose}>
                    {ADMIN_LANG.close}
                </Button>
                <Button variant='primary' type='submit' form='user-modal-form'>
                    {ADMIN_LANG.save}
                </Button>
            </Modal.Footer>
            <Confirm
                show={showConfirmDelete}
                message={delete_message}
                onConfirm={handleConfirm}
                onCancel={() => setShowConfirmDelete(false)} />
        </Modal>
    );
};

export default UserModal;
